import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { FiAiChatHelperService } from '@fi-shared/fi-ai-chat/fi-ai-chat-helper.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { map, takeUntil } from 'rxjs/operators';
import { PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';

@Component({
    selector: 'fi-chat-button',
    template: `
        <audio hidden #audioElement crossorigin="anonymous" preload="auto" [src]="getAudioSrc()"></audio>

        @if (!isMobileApp) {
            <button
                mat-fab
                color="primary"
                class="ContrastBackgroundColor chat-mat-icon-button"
                (click)="openConversationListClick()"
            >
                <mat-icon class="fa fa-comments fa-lg"></mat-icon>
            </button>
            @if (newConversationsCount$ | async; as newConversationsCount) {
                <span class="notifications-count">
                    {{ newConversationsCount }}
                </span>
            }
        }

        @if (isMobileApp) {
            <button mat-icon-button (click)="openConversationListClick()">
                <mat-icon class="fas fa-comments"></mat-icon>
                @if (newConversationsCount$ | async; as newConversationsCount) {
                    <span class="notifications-count mobile-icon">
                        {{ newConversationsCount }}
                    </span>
                }
            </button>
        }
    `,
    styleUrls: ['./fi-chat-button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-button' },
    standalone: false,
})
export class FiChatButtonComponent implements OnInit, OnDestroy {
    @HostBinding('class.mobile-app')
    @Input()
    isMobileApp = false;

    @HostBinding('class.hidden')
    isButtonHidden: boolean;

    @HostBinding('style.right.px')
    rightOffset: number | null = null;

    @ViewChild('audioElement') audioElementRef: ElementRef<HTMLAudioElement>;
    get newConversationsCount$(): Observable<number> {
        return this.fiChatSyncService.conversationsUpdated$.pipe(
            map(($event) => {
                return this.fiChatSyncService.newConversations.length;
            }),
        );
    }

    private animationendListener: () => void;
    private closeEmojiOverlayFn: () => void;

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private fiChatSyncService: FiChatSyncService,
        private fiBackend: FiBackendService,
        private fiSignalRService: FiSignalRService,
        private userDataService: FiUserDataService,
        private elementRef: ElementRef,
        private renderer2: Renderer2,
        private fiEmojiService: FiEmojiService,
        private detectDevicesService: DetectDevicesService,
        private fiAiChatHelperService: FiAiChatHelperService,
    ) {}

    ngOnInit(): void {
        if (this.fiChatHelperService.hasAccessToChat()) {
            this.newMessagesSubscribe();
            this.setAnimationEndListener();
            this.fiChatService.subscribeToSignalREvents();
            this.fiChatService.startConversationsChangesTimer();
            if (!this.detectDevicesService.isMobilePhone()) {
                this.closeEmojiOverlayFn = this.fiEmojiService.openEmojiOverlay();
            }
            this.subscribeToLayoutShift();
        } else {
            this.isButtonHidden = true;
        }
    }

    /**
     * fi-chat-button is normally covered by fi-chat panel when it's shown (z-index lets the panel sit on top).
     * When AI chat panel is open and fi-chat is NOT shown, we shift this button to the left of the AI panel
     * so it stays visible. Otherwise default position (right:48px from scss) applies.
     */
    private subscribeToLayoutShift() {
        if (this.isMobileApp) return;
        combineLatest([
            this.fiChatHelperService.showConversationList$,
            this.fiChatHelperService.activeConversationsCount$,
            this.fiAiChatHelperService.isOpen$,
        ])
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(([listShown, activeCount, aiOpen]) => {
                const fiChatShown = listShown || activeCount > 0;
                if (!fiChatShown && aiOpen) {
                    // AI panel sits at right:25 width:400 -> covers up to right:425.
                    // Shift fi-chat-button to right:440 (15px gap to the left of AI panel).
                    this.rightOffset =
                        FiAiChatHelperService.PANEL_WIDTH +
                        FiAiChatHelperService.PANEL_DEFAULT_RIGHT +
                        FiAiChatHelperService.PANEL_GAP;
                } else {
                    this.rightOffset = null; // fall back to scss default (48px)
                }
            });
    }

    private setAnimationEndListener() {
        this.animationendListener = this.renderer2.listen(this.elementRef.nativeElement, 'animationend', () => {
            this.elementRef.nativeElement.classList.remove('animate__animated', 'animate__bounce');
        });
    }

    openConversationListClick() {
        this.fiChatHelperService.raiseOpenConversationList();
    }

    getAudioSrc() {
        return this.fiBackend.getRequestUrl('Content/Sounds/new_message_notification.mp3');
    }

    private newMessagesSubscribe() {
        this.fiSignalRService.chatConversationMessageUpdatedEvent.subscribe((data) => {
            if (data.CurrentUserId !== this.userDataService.getUserId()) {
                this.audioElementRef.nativeElement.play().catch((err) => console.error(err));
                this.animateChatButton();
            }
        }, this.ngOnDestroy$);
    }

    private animateChatButton() {
        this.elementRef.nativeElement.classList.add('animate__animated', 'animate__bounce');
    }

    @HostListener('document:resume')
    onDocumentResume() {
        this.fiChatService.checkConversationsChanges();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        if (this.animationendListener) {
            this.animationendListener();
        }

        if (this.closeEmojiOverlayFn) {
            this.closeEmojiOverlayFn();
        }
    }
}
